import React, { FormEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import SocialButtons from './AccountAccessSocialButtons'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import { checkUserExists, getUserKeyFromPhone, loginApple } from 'actions/AuthActions'
import TextButton from 'LuxKit/Button/TextButton'
import ReCAPTCHA from 'react-google-recaptcha'
import config from 'constants/config'
import BodyText from 'LuxKit/Typography/BodyText'
import AccountAccessLegalText from './AccountAccessLegalText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import TrustIndicatorsMapper from './AccountAccessTrustIndicatorsMapper'
import LineHeartIcon from 'LuxKit/Icons/line/LineHeartIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineUniversityIcon from 'LuxKit/Icons/line/LineUniversityIcon'
import noop from 'lib/function/noop'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { rem } from 'polished'
import styled from 'styled-components'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import * as Analytics from 'analytics/analytics'
import AccountAccessOfferPreview from './AccountAccessOfferPreview'
import { isCheckout } from 'selectors/checkoutSelectors'
import AccountAccessUserInput from './AccountAccessUserInput'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import useLuxPlusFreePreviewUtils from 'luxPlus/hooks/useLuxPlusFreePreviewUtils'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { AccountAccessModalResult } from './AccountAccessModal'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import AccountAccessTourCatalogueHeader from 'components/Account/AccountAccess/AccountAccessTourCatalogueHeader'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import useQueryParams from 'hooks/useQueryParams'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import { getLastUsedLoginMethod } from 'lib/auth/lastUsedLoginMethod'
import { useIntl, IntlShape } from 'react-intl'

export interface LoginForm {
  email?: string
  phoneNumber?: App.PhoneNumber
}

const Or = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};

  &::after,
  &::before {
    content: '';
    flex: 1;
    border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
    display: block;
  }
`

type LoginUIContent = Array<{
  Icon: React.ReactElement
  text: string
}>

type LoginUIData = {
  title: string
  content: LoginUIContent
}

interface GetLoginUIData {
  intl: IntlShape
  isBookingFlow: boolean
  autoEnrollFreePreview: boolean
  mode?: App.UiAccountModalMode
  loyaltyEnabled: boolean
  enabledBenefits: App.LoyaltyRegionEnabledBenefits | undefined
  isMerchandise: boolean
  loyaltyProgramDisplayName: string
  bookingAuthorityOwnerName?: string
}

const getLoginUIData = ({
  intl,
  isBookingFlow,
  autoEnrollFreePreview,
  mode,
  loyaltyEnabled,
  enabledBenefits,
  isMerchandise,
  loyaltyProgramDisplayName,
  bookingAuthorityOwnerName,
}: GetLoginUIData): LoginUIData => {
  if (mode === 'tripPlannerLogin') {
    return {
      title: intl.formatMessage({
        id: '3l+a5htDhx',
        defaultMessage: 'Sign up to start planning your next escape',
        description: 'Title for trip planner login modal',
      }),
      content: [
        {
          Icon: <LineHeartIcon />,
          text: intl.formatMessage({
            id: 'CGTNd4v44l',
            defaultMessage: 'Save your favourite offers',
            description: 'Benefit text for saving favourite offers',
          }),
        },
        {
          Icon: <LineCalendarIcon />,
          text: intl.formatMessage({
            id: 'w0uR3oi31g',
            defaultMessage: 'Easily plan your perfect trip',
            description: 'Benefit text for planning trips',
          }),
        },
        {
          Icon: <LineUniversityIcon />,
          text: intl.formatMessage({
            id: '0ZNOOUcncM',
            defaultMessage: 'Explore must-see attractions',
            description: 'Benefit text for exploring attractions',
          }),
        },
      ],
    }
  } else if (mode === 'tripPlannerPagesLogin') {
    return {
      title: intl.formatMessage({
        id: 'CWx0YeajF/',
        defaultMessage: "You're one step away from your dream itinerary",
        description: 'Title for trip planner pages login modal',
      }),
      content: [
        {
          Icon: <LineHeartIcon />,
          text: intl.formatMessage({
            id: 'CGTNd4v44l',
            defaultMessage: 'Save your favourite offers',
            description: 'Benefit text for saving favourite offers',
          }),
        },
        {
          Icon: <LineCalendarIcon />,
          text: intl.formatMessage({
            id: 'w0uR3oi31g',
            defaultMessage: 'Easily plan your perfect trip',
            description: 'Benefit text for planning trips',
          }),
        },
        {
          Icon: <LineUniversityIcon />,
          text: intl.formatMessage({
            id: '0ZNOOUcncM',
            defaultMessage: 'Explore must-see attractions',
            description: 'Benefit text for exploring attractions',
          }),
        },
      ],
    }
  } else if (mode === 'requestAgentCallbackLogin') {
    return {
      title: intl.formatMessage({
        id: 'U1px89GVqq',
        defaultMessage: 'Log in to request a callback',
        description: 'Title for request agent callback login modal',
      }),
      content: [
        {
          Icon: <LineGiftIcon />,
          text: intl.formatMessage({
            id: '9r229xPEq9',
            defaultMessage: 'Unlock thousands of exclusive offers',
            description: 'Benefit text for unlocking exclusive offers',
          }),
        },
        {
          Icon: <LineStarIcon />,
          text: intl.formatMessage({
            id: 'C9WWbg7Zuk',
            defaultMessage: 'Over a million trips taken every year',
            description: 'Benefit text about trips taken',
          }),
        },
        {
          Icon: <LinePhoneIcon />,
          text: intl.formatMessage({
            id: 'iSAwLVS7R8',
            defaultMessage: 'Our local team are available 24 hours, 7 days',
            description: 'Benefit text about local team availability',
          }),
        },
      ],
    }
  } else if (mode === 'supportLogin') {
    return {
      title: intl.formatMessage({
        id: 'htBY8iDi1O',
        defaultMessage: 'Log in or sign up so we can help',
        description: 'Title for support login modal',
      }),
      content: [],
    }
  } else if (autoEnrollFreePreview) {
    return {
      title: intl.formatMessage({
        id: 'cF4OYkUPFe',
        defaultMessage: 'Sign in to access your exclusive {programName} free preview',
        description: 'Title for auto-enroll free preview login modal',
      }, {
        programName: LUXURY_PLUS.PROGRAM_NAME,
      }),
      content: [],
    }
  }
  else if (mode === 'loyaltyLogin') {
    const content = [
      {
        Icon: <LineBedIcon />,
        text: intl.formatMessage({
          id: 'C4BqyZG9wM',
          defaultMessage: 'Upgrade your stay',
          description: 'Benefit text for upgrading stay',
        }),
      },
      {
        Icon: <LineTaxiIcon />,
        text: intl.formatMessage({
          id: 'XGBRtGAuQE',
          defaultMessage: 'Start your escape with chauffeur transfers',
          description: 'Benefit text for chauffeur transfers',
        }),
      },
    ]

    if (enabledBenefits?.lounge_pass) {
      content.push({
        Icon: <LineChairWithAirplaneIcon />,
        text: intl.formatMessage({
          id: '3stURqq3wB',
          defaultMessage: 'Lounge in comfort before your flight',
          description: 'Benefit text for lounge pass',
        }),
      })
    }

    return {
      title: intl.formatMessage({
        id: 'da++mD8JIx',
        defaultMessage: 'Join {programName} and elevate your escapes',
        description: 'Title for loyalty login modal',
      }, {
        programName: loyaltyProgramDisplayName,
      }),
      content,
    }
  }
  else if (mode === 'sharedCartInvite') {
    return {
      title: intl.formatMessage({
        id: 'bqSS2SmCr6',
        defaultMessage: 'You have been invited to join a shared cart',
        description: 'Title for shared cart invite login modal',
      }),
      content: [],
    }
  }
  else if (mode === 'bookingAuthorityInvite') {
    if (bookingAuthorityOwnerName) {
      return {
        title: intl.formatMessage({
          defaultMessage: '{ownerName} has invited you to their trip.',
          id: 'Un4nQXL59D',
          description: 'Title for booking authority invite login modal when invite owner name is known',
        }, { ownerName: bookingAuthorityOwnerName }),
        content: [],
      }
    }

    return {
      title: intl.formatMessage({
        defaultMessage: "You've been invited to a booking",
        id: 'Jfa67QG2gh',
        description: 'Fallback title for booking authority invite login modal when invite owner name is not provided',
      }),
      content: [],
    }
  }
  else if (isMerchandise) {
    return {
      title: intl.formatMessage({
        id: 'rAQyOIovsN',
        defaultMessage: 'Log in or sign up to view this product',
        description: 'Title for merchandise login modal',
      }),
      content: [],
    }
  }
  else {
    return {
      title: isBookingFlow ? intl.formatMessage({
        id: 'yQbwz4+ule',
        defaultMessage: 'Log in or sign up to book this exclusive member offer',
        description: 'Title for booking flow login modal',
      }) : intl.formatMessage({
        id: 'fPADrgrXqJ',
        defaultMessage: 'Exclusive prices and more with every booking',
        description: 'Default title for login modal',
      }),
      content: [
        {
          Icon: <LineGiftIcon />,
          text: intl.formatMessage({
            id: '9r229xPEq9',
            defaultMessage: 'Unlock thousands of exclusive offers',
            description: 'Benefit text for unlocking exclusive offers',
          }),
        },
        {
          Icon: <LineStarIcon />,
          text: loyaltyEnabled ? intl.formatMessage({
            id: 'ImTrrx9VbM',
            defaultMessage: 'Earn & redeem points with every booking',
            description: 'Benefit text for earning and redeeming points',
          }) : intl.formatMessage({
            id: 'C9WWbg7Zuk',
            defaultMessage: 'Over a million trips taken every year',
            description: 'Benefit text about trips taken',
          }),
        },
        {
          Icon: <LinePhoneIcon />,
          text: intl.formatMessage({
            id: 'iSAwLVS7R8',
            defaultMessage: 'Our local team are available 24 hours, 7 days',
            description: 'Benefit text about local team availability',
          }),
        },
      ],
    }
  }
}

interface Props {
  error?: any
  isStoreMode: boolean
  mode?: App.UiAccountModalMode
  onModeChange?: (mode: App.UiAccountModalMode, userId?: string) => void
  hotelOfferDetails?: App.AccountModalOfferDetails
  offerDetails?: App.AccountModalOfferDetails
  dismissable?: boolean
  applePrivateEmailData: App.AppleLoginData | null
  setApplePrivateEmailData: (loginData: App.AppleLoginData) => void
}

function AccountAccessLoginStart(props: Props) {
  const {
    error,
    isStoreMode,
    mode,
    onModeChange = noop,
    hotelOfferDetails,
    offerDetails,
    dismissable,
    applePrivateEmailData,
    setApplePrivateEmailData,
  } = props

  const formRef = useRef<HTMLFormElement>(null)
  const [userId, setUserId] = useState<string>('')
  const processing = useAppSelector(state => state.auth.processing)
  const user = useAppSelector(state => state.auth.users[userId])
  const isBooking = useAppSelector(isCheckout)
  const { shouldAutoEnrolFreePreview: autoEnrollFreePreview } = useLuxPlusFreePreviewUtils()
  const loggedIn = useAppSelector(selectLoggedIn)
  const loyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const modalContext = useModalElementContext<AccountAccessModalResult>()
  const query = useQueryParams()
  const dispatch = useAppDispatch()
  const lastUsedLoginMethod = getLastUsedLoginMethod()
  const intl = useIntl()
  const loyaltyProgramDisplayName = useAppSelector(getLoyaltyProgramDisplayName)

  useEffect(() => {
    if (!processing && user) {
      if (user.exists) {
        // Is Apple private email with LE account
        if (applePrivateEmailData) {
          dispatch(loginApple(applePrivateEmailData))
        } else {
          onModeChange(user.maskedPhone ? 'loginPasswordOTP' : 'loginPassword', user.id)
        }
      } else {
        if (applePrivateEmailData && user.id?.endsWith('@privaterelay.appleid.com')) {
          onModeChange('applePrivateEmail', user.id)
        } else {
          onModeChange('join', user.id)
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, processing])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'sign_up_appear',
      action: 'impression',
      category: 'logging',
      type: 'operational',
    })
  }, [])

  useEffect(() => {
    if (loggedIn) {
      modalContext?.resolve({ loggedIn: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn])

  const reRef = useRef<ReCAPTCHA>(null)

  const onPrivateAppleEmailLogin = useCallback((appleLoginData: App.AppleLoginData, email: string) => {
    setUserId(email)
    setApplePrivateEmailData(appleLoginData)
    dispatch(checkUserExists({ email }, reRef.current ?? undefined))
  }, [dispatch, setApplePrivateEmailData])

  const onCheckEmail = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    const loginData = formToObject<LoginForm>(event.currentTarget)
    Analytics.trackClientEvent({
      subject: 'sign_up_appear_continue',
      action: 'interaction',
      category: loginData.phoneNumber ? 'phone' : 'email',
      type: 'operational',
    })
    const userId = loginData.phoneNumber ? getUserKeyFromPhone(loginData.phoneNumber) : loginData.email
    setUserId(userId ?? '')
    dispatch(checkUserExists(loginData, reRef.current ?? undefined))
  }, [dispatch])

  const isSharedCartInvite = mode === 'sharedCartInvite'
  const isBookingAuthorityInvite = mode === 'bookingAuthorityInvite'
  const isTourCatalogue = mode === 'toursCatalogueLogin'
  const socialMediaLoginsEnabled = config.FACEBOOK_APP_ID || config.GOOGLE_APP_ID || config.APPLE_APP_ID
  // `hide_sso` is used for some deep links that want to suppress SSO buttons, however
  // booking authority invite should always allow other login methods where configured.
  const skipSocialQuery = query.has('hide_sso') && !isBookingAuthorityInvite
  const showSocialMediaButtons = !isStoreMode && socialMediaLoginsEnabled && !skipSocialQuery

  const isBookingFlow = isBooking || !!hotelOfferDetails || !!offerDetails

  const [loyaltyConfig] = useLoyaltyConfig()

  const bookingAuthorityOwnerName = query.get('ownerName') ?? undefined

  const { title, content } = useMemo(() => getLoginUIData({
    intl,
    isBookingFlow,
    autoEnrollFreePreview,
    mode,
    loyaltyEnabled,
    enabledBenefits: loyaltyConfig?.regionEnabledBenefits,
    isMerchandise: !!offerDetails?.merchandiseVariant,
    loyaltyProgramDisplayName,
    bookingAuthorityOwnerName,
  }), [intl, isBookingFlow, autoEnrollFreePreview, mode, loyaltyEnabled, offerDetails?.merchandiseVariant, loyaltyConfig?.regionEnabledBenefits, loyaltyProgramDisplayName, bookingAuthorityOwnerName])

  const showTrustIndicators = config.LOGIN_TRUST_INDICATORS && !isBookingFlow && !autoEnrollFreePreview && !isTourCatalogue && !isBookingAuthorityInvite

  return (<>
    <ModalHeader title={title} dismissible={dismissable} isOverlay={isTourCatalogue} />
    <ModalBody>
      {isTourCatalogue && <AccountAccessTourCatalogueHeader />}
      <ModalContent flushVerticalTop={!!isBookingAuthorityInvite}>
        <VerticalSpacer gap={24}>
          <VerticalSpacer ref={formRef} gap={24} as="form" onSubmit={onCheckEmail} name="loginForm">
            {error?.message && <MessageBanner kind="critical">{error.message}</MessageBanner>}
            {isBookingFlow && <AccountAccessOfferPreview hotelOfferDetails={hotelOfferDetails} offerDetails={offerDetails} />}
            {isSharedCartInvite && <BodyText variant="medium">{intl.formatMessage({
              id: 'pB1OB/6te3',
              defaultMessage: 'Please log in or create an account to securely access this cart.',
              description: 'Description text for shared cart invite',
            })}
            </BodyText>}
            {!isBookingFlow && !isTourCatalogue && !isSharedCartInvite && !isBookingAuthorityInvite && (
              <Group direction="vertical" gap={20}>
                <Group direction="vertical" gap={4}>
                  {content.map(({ Icon, text }) => <BodyTextBlock
                    key={text}
                    variant="medium"
                    startIcon={Icon}
                  >
                    {text}
                  </BodyTextBlock>)}
                </Group>
              </Group>
            )}
            {isBookingAuthorityInvite && (
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'px4hOT8QgE',
                  defaultMessage: 'Please log in or create an account to securely access this booking.',
                  description: 'Description text for booking authority invite',
                })}
              </BodyText>
            )}
            {showTrustIndicators && <Group direction="horizontal" horizontalAlign="center">
              <TrustIndicatorsMapper />
            </Group>}
            <Group direction="vertical" gap={32} tabletGap={24}>
              <AccountAccessUserInput showLastUsedTag={lastUsedLoginMethod === 'email' || lastUsedLoginMethod === 'phone' || lastUsedLoginMethod === 'otp'} />
              <TextButton
                kind="primary"
                size="large"
                type="submit"
              >
                {intl.formatMessage({
                  id: '3s73DJen/2',
                  defaultMessage: 'Continue',
                  description: 'Continue button text',
                })}
              </TextButton>
            </Group>
            {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reRef} />}
          </VerticalSpacer>
          {showSocialMediaButtons && <>
            <Or>
              <BodyText colour="neutral-two" variant="medium">{intl.formatMessage({
                id: 'ehOG6vfry6',
                defaultMessage: 'Other ways to log in',
                description: 'Text for social login section divider',
              })}
              </BodyText>
            </Or>
            <SocialButtons
              mode="login"
              inline
              onPrivateAppleEmailLogin={onPrivateAppleEmailLogin}
              lastUsedLoginMethod={lastUsedLoginMethod}
            />
          </>}
          <AccountAccessLegalText />
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
  </>)
}

function mapStateToProps(state: App.State) {
  return {
    error: state.auth.error,
    isStoreMode: state.system.storeMode,
  }
}

export default connect(mapStateToProps)(AccountAccessLoginStart)
